import type { DSAttendancePrefillEmptyReason } from '@/Network/Types/Responses/DsAttendancePolicyResponses'
import {
  DSAttendanceRuleRecurrence,
  DSATTENDANCE_RECURRENCE_OPTIONS
} from '@/constants/ds-attendance.constants'

/**
 * Fase 1 do fluxo de auditoria e conciliação (spec 2026-09-11, §5.2/§5.3).
 *
 * Puro de propósito: o mesmo texto aparece no chip do card da fila, no chip do cabeçalho da
 * auditoria e no aviso que substitui a grade do espelho vazia. Manter a montagem aqui deixa
 * o texto testável sem montar componente e impede que as três telas divirjam.
 */

/** Texto do chip cinza, igual na fila e no cabeçalho da auditoria. */
export const NO_SCHEDULE_EXPECTED_LABEL = 'Sem escala prevista'

const NOT_INFORMED = 'não informada'
const SHIFTS_NOT_INFORMED = 'não informados'

/**
 * Tooltip do chip. Explica os dois efeitos da marcação: sai da fila padrão e para de contar
 * prazo — sem isso o auditor lê "sem escala prevista" como um erro do sistema.
 */
export function buildNoScheduleExpectedTooltip(params: {
  scheduleName?: string | null
  date?: string | null
  shifts?: string | null
}): string {
  const schedule = params.scheduleName?.trim() || NOT_INFORMED
  const date = params.date?.trim() || NOT_INFORMED
  const shifts = params.shifts?.trim() || SHIFTS_NOT_INFORMED
  return (
    `A escala ${schedule} não tem plantão com médico em ${date} nos turnos ${shifts}. ` +
    'O registro fica fora da fila e do prazo até a escala ser preenchida.'
  )
}

/** Pedaço de texto do aviso; `strong` marca o que o design destaca em negrito. */
export interface PrefillEmptyStateSegment {
  text: string
  strong?: boolean
}

export interface PrefillEmptyState {
  reason: DSAttendancePrefillEmptyReason
  /** Texto corrido, sem marcação — serve para `title`, leitor de tela e asserção de teste. */
  text: string
  /** Mesmo texto quebrado para render: `strong: true` vira `<strong>`. */
  segments: PrefillEmptyStateSegment[]
  /** Só o motivo da data oferece o atalho "Ver escala do dia". */
  showScheduleLink: boolean
}

export interface PrefillEmptyStateParams {
  /** Ausente/`null` (backend anterior à fase 1) ⇒ sem aviso: a tela segue silenciosa. */
  emptyReason?: DSAttendancePrefillEmptyReason | null
  scheduleName?: string | null
  date?: string | null
  shifts?: string | null
  /** Recorrência da regra da política; quando não vier, o parêntese some do texto. */
  recurrence?: DSAttendanceRuleRecurrence | string | null
}

/** Rótulo pt-BR da recorrência; valor desconhecido volta cru em vez de sumir. */
export function recurrenceLabel(recurrence?: string | null): string | null {
  if (!recurrence) return null
  const option = DSATTENDANCE_RECURRENCE_OPTIONS.find((item) => item.value === recurrence)
  return option?.title ?? recurrence
}

function segmentsToText(segments: PrefillEmptyStateSegment[]): string {
  return segments.map((segment) => segment.text).join('')
}

function buildNoStationsOnDate(params: PrefillEmptyStateParams): PrefillEmptyStateSegment[] {
  const schedule = params.scheduleName?.trim() || NOT_INFORMED
  const date = params.date?.trim() || NOT_INFORMED
  const shifts = params.shifts?.trim() || SHIFTS_NOT_INFORMED
  return [
    { text: 'Nenhum plantão previsto em ' },
    { text: schedule, strong: true },
    { text: ' em ' },
    { text: date, strong: true },
    { text: ` (${shifts}). A folha será digitada sem espelho.` }
  ]
}

function buildPeriodNotSupported(params: PrefillEmptyStateParams): PrefillEmptyStateSegment[] {
  const label = recurrenceLabel(params.recurrence)
  const suffix = label ? ` (${label})` : ''
  return [
    {
      text:
        `Registro de período${suffix}: o espelho ainda não cobre folhas mensais ou quinzenais. ` +
        'A folha será digitada sem espelho.'
    }
  ]
}

function buildNoStationsForShifts(params: PrefillEmptyStateParams): PrefillEmptyStateSegment[] {
  const shifts = params.shifts?.trim() || SHIFTS_NOT_INFORMED
  return [
    {
      text: `Há plantão nesta data, mas em turnos diferentes dos deste registro (${shifts}).`
    }
  ]
}

const SEGMENT_BUILDERS: Record<
  DSAttendancePrefillEmptyReason,
  (params: PrefillEmptyStateParams) => PrefillEmptyStateSegment[]
> = {
  NO_STATIONS_ON_DATE: buildNoStationsOnDate,
  PERIOD_NOT_SUPPORTED: buildPeriodNotSupported,
  NO_STATIONS_FOR_SHIFTS: buildNoStationsForShifts
}

/**
 * Monta o aviso do espelho vazio. Devolve `null` quando não há motivo — é o caso do backend
 * anterior à fase 1, em que a tela cai na digitação clássica em silêncio, como sempre fez.
 */
export function buildPrefillEmptyState(params: PrefillEmptyStateParams): PrefillEmptyState | null {
  const reason = params.emptyReason
  if (!reason) return null

  const build = SEGMENT_BUILDERS[reason]
  // Motivo que o front ainda não conhece (backend à frente): melhor silêncio que texto errado.
  if (!build) return null

  const segments = build(params)
  return {
    reason,
    text: segmentsToText(segments),
    segments,
    showScheduleLink: reason === 'NO_STATIONS_ON_DATE'
  }
}
